import React, { useMemo } from 'react';
import { useOptimizedPublisherAnalysis } from './context/OptimizedPublisherAnalysisContext';
import './style/gameCards.scss';

const GameCards = () => {
  const { gameCards, loader, publisher, categoryApps } = useOptimizedPublisherAnalysis();
  
  // Compute averages from categoryApps (all games under this publisher)
  const { averageRating, averageAppScore, totalRatingCount } = useMemo(() => {
    const games = Array.isArray(categoryApps) ? categoryApps : [];

    // For rating we prefer weighted average when total counts are available
    let weightedRatingSum = 0;
    let weightedRatingCount = 0;

    // Fallback unweighted accumulation
    let unweightedRatingSum = 0;
    let unweightedRatingCount = 0;

    let scoreSum = 0;
    let scoreCount = 0;

    games.forEach(g => {
      const ratingObj = g?.rating;
      if (ratingObj && (ratingObj.average !== undefined || ratingObj.avg_rating !== undefined)) {
        const avg = ratingObj.average ?? ratingObj.avg_rating ?? null;
        const total = ratingObj.total_rating ?? null;
        const parsedAvg = typeof avg === 'number' ? avg : parseFloat(avg);
        const parsedTotal = typeof total === 'number' ? total : parseInt(total);

        if (!isNaN(parsedAvg) && parsedTotal > 0) {
          weightedRatingSum += parsedAvg * parsedTotal;
          weightedRatingCount += parsedTotal;
        } else if (!isNaN(parsedAvg)) {
          // fall back to unweighted if no total
          unweightedRatingSum += parsedAvg;
          unweightedRatingCount += 1;
        }
      } else {
        // older data shapes or simple numeric fields
        const r = g?.rating ?? g?.avg_rating ?? g?.average_rating;
        const parsedR = typeof r === 'number' ? r : parseFloat(r);
        if (!isNaN(parsedR)) {
          unweightedRatingSum += parsedR;
          unweightedRatingCount += 1;
        }
      }

      // app score may be stored as app_score, appScore, score
      const s = g?.app_score ?? g?.appScore ?? g?.score ?? g?.keyquest_score;
      const parsedS = typeof s === 'number' ? s : parseFloat(s);
      if (!isNaN(parsedS)) {
        scoreSum += parsedS;
        scoreCount += 1;
      }
    });

    // Combine weighted and unweighted: if we have weighted counts use them; otherwise fall back to unweighted mean
    const avgRating = weightedRatingCount > 0
      ? (weightedRatingSum / weightedRatingCount)
      : (unweightedRatingCount > 0 ? (unweightedRatingSum / unweightedRatingCount) : null);

    const totalRatings = weightedRatingCount > 0 ? weightedRatingCount : unweightedRatingCount;

    const avgScore = scoreCount > 0 ? (scoreSum / scoreCount) : null;

    return {
      averageRating: avgRating,
      averageAppScore: avgScore,
      totalRatingCount: totalRatings
    };
  }, [categoryApps]);

  if (loader) {
    return (
      <div className="game-cards">
        <div className="game-card">
          <h2>Most Downloaded Game</h2>
          <div className="game-info">
            <div className="placeholder-glow">
              <span className="placeholder col-6"></span>
            </div>
          </div>
        </div>
        <div className="game-card">
          <h2>Highest Grossing App</h2>
          <div className="game-info">
            <div className="placeholder-glow">
              <span className="placeholder col-6"></span>
            </div>
          </div>
        </div>
        <div className="game-card">
          <h2>Average Rating</h2>
          <div className="stat-placeholder">
            <div className="placeholder-glow">
              <span className="placeholder col-6"></span>
            </div>
          </div>
        </div>
        <div className="game-card">
          <h2>Average App Score</h2>
          <div className="stat-placeholder">
            <div className="placeholder-glow">
              <span className="placeholder col-6"></span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Ensure publisher exists and has a name
  const publisherName = publisher?.name || '';
 
  
  return (
    <div className="game-cards">
      <div className="game-card">
        <h2>Most downloaded Game</h2>
        <div className="game-info">
          {gameCards.mostDownloaded.image ? (
            <img 
              src={gameCards.mostDownloaded.image} 
              alt={gameCards.mostDownloaded.name} 
            />
          ) : (
            <div className="placeholder-image">
              <i className="fas fa-gamepad"></i>
            </div>
          )}
          <div className="game-details">
            <span className="game-name">{gameCards.mostDownloaded.name || 'N/A'}</span>
            
          </div>
        </div>
      </div>
      <div className="game-card stat-card">
        <h2>Average Rating</h2>
        <div className="stat">
          <div className="stat-value">
            {averageRating !== null ? averageRating.toFixed(1) : 'N/A'}
          </div>
          <div className="stat-desc">{totalRatingCount ? `Based on ${totalRatingCount.toLocaleString()} ratings` : 'Based on all games under this publisher'}</div>
        </div>
      </div>

      <div className="game-card stat-card">
        <h2>Average App Score</h2>
        <div className="stat">
          <div className="stat-value">
            {averageAppScore !== null ? averageAppScore.toFixed(1) : 'N/A'}
          </div>
          <div className="stat-desc">Proprietary KeyQuest score</div>
        </div>
      </div>
    </div>
  );
};

export default GameCards;
